/* eslint-disable @nx/enforce-module-boundaries */
import { FormErrorMessage } from '@chakra-ui/react';
import { CirclePlus } from '@mybridge/icons';
import {
  Button,
  FormControl,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  useDisclosure,
} from '@mybridge/ui';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { Input } from '@mybridge/ui/input';
import { useEffect, useRef, useState } from 'react';
import { formatCommaSeparated } from 'v4/lib/commons';

export const TagsInput = ({
  value,
  defaultValue,
  onChange,
  maxLength = 50,
  title,
  dropdown,
  unique = false,
  children,
  renderFn = (v) => v,
  buttonProps,
  ...props
}) => {
  const { t } = useTranslator();
  const [values, setValues] = useState(defaultValue ?? []);
  const [err, setErr] = useState();
  const [str, setStr] = useState();
  const [edit, setEdit] = useState(false);
  const popDisc = useDisclosure();
  const inputRef = useRef();

  useEffect(() => {
    if (JSON.stringify(values) !== JSON.stringify(value)) {
      setValues(value);
    }
  }, [value]);

  const addTag = (tag) => {
    // Handle objects (when renderFn is provided, objects are expected)
    if (typeof tag === 'object' && tag !== null) {
      // Helper function to check if two objects are the same
      const isSameObject = (obj1, obj2) => {
        // Compare by id if both have id (for industries)
        if (obj1?.id && obj2?.id) {
          return obj1.id === obj2.id;
        }
        // Compare by name if both have name (for industries)
        if (obj1?.name && obj2?.name) {
          return obj1.name === obj2.name;
        }
        // For locations: compare by city, state/state_region, and country
        if ((obj1?.city || obj1?.state || obj1?.state_region || obj1?.country) &&
            (obj2?.city || obj2?.state || obj2?.state_region || obj2?.country)) {
          return (
            (obj1?.city || '') === (obj2?.city || '') &&
            (obj1?.state || obj1?.state_region || '') === (obj2?.state || obj2?.state_region || '') &&
            (obj1?.country || '') === (obj2?.country || '')
          );
        }
        return false;
      };
      
      // Check if tag already exists
      const exists = values?.find?.((v) => {
        if (typeof v === 'object' && v !== null) {
          return isSameObject(tag, v);
        }
        return false;
      });
      
      // Always prevent duplicates for objects
      if (exists) {
        return;
      }
      
      setTags([...(values ?? []), tag]);
      return;
    }
    
    // Handle strings (original behavior)
    if (!tag || typeof tag !== 'string' || tag.trim() === '') {
      return;
    }
    const trimmedTag = tag.trim();
    // Always prevent duplicates for strings
    if (values?.find?.((t) => t === trimmedTag)) {
      return;
    }
    setTags([...(values ?? []), trimmedTag]);
  };
  const removeTag = (tag) => {
    // Handle objects
    if (typeof tag === 'object' && tag !== null) {
      // Helper function to check if two objects are the same
      const isSameObject = (obj1, obj2) => {
        // Compare by id if both have id (for industries)
        if (obj1?.id && obj2?.id) {
          return obj1.id === obj2.id;
        }
        // Compare by name if both have name (for industries)
        if (obj1?.name && obj2?.name) {
          return obj1.name === obj2.name;
        }
        // For locations: compare by city, state/state_region, and country
        if ((obj1?.city || obj1?.state || obj1?.state_region || obj1?.country) &&
            (obj2?.city || obj2?.state || obj2?.state_region || obj2?.country)) {
          return (
            (obj1?.city || '') === (obj2?.city || '') &&
            (obj1?.state || obj1?.state_region || '') === (obj2?.state || obj2?.state_region || '') &&
            (obj1?.country || '') === (obj2?.country || '')
          );
        }
        return false;
      };
      
      const filtered = values?.filter?.((v) => {
        if (typeof v === 'object' && v !== null) {
          return !isSameObject(tag, v);
        }
        return v !== tag;
      });
      setTags(filtered);
      return;
    }
    
    // Handle strings (original behavior)
    setTags(values?.filter((v) => v !== tag));
  };

  const onKeyDown = (e) => {
    if (e.keyCode !== 13 && e.keyCode !== 27) return;
    e.preventDefault?.();
    e.stopPropagation?.();
  };

  const onKeyUp = (e) => {
    e.preventDefault?.();
    e.stopPropagation?.();
    if (e.keyCode === 27) {
      setStr('');
      inputRef.current.value = '';
      setEdit(false);
    }
    if (e.keyCode === 13) {
      const value = e.target.value?.trim();
      if (value) {
        addTag(value);
        setStr('');
        inputRef.current.value = '';
        setEdit(false);
      }
    }
  };

  const onTextBlur = (e) => {
    e.preventDefault?.();
    e.stopPropagation?.();
    const value = e.target.value?.trim();
    if (value) {
      addTag(value);
      setStr('');
      inputRef.current.value = '';
      setEdit(false);
    }
  };

  const setTags = (tags) => {
    setValues(tags);
    onChange?.(tags);
  };

  return (
    <Stack>
      <HStack flexWrap="wrap">
        {values?.length ? (
          <>
            {values?.filter((v,i,arr) => arr.indexOf(v)===i)?.map?.((v, vIndex) => (
              <Tag
                style={{ '--badge-color': '#CCCCCC' }}
                border="1px solid"
                borderRadius="full"
                p={3}
                key={vIndex}
                bg="brandGray.200"
                maxW="100%"
              >
                <TagLabel fontSize={['12px', 'md']} color="blackAlpha.700" pl={2} isTruncated>
                  {renderFn(v)}
                </TagLabel>
                <TagCloseButton
                  color="black"
                  onClick={(e) => removeTag(v)}
                  pl={1}
                  pr={2}
                />
              </Tag>
            ))}
          </>
        ) : (
          ''
        )}
        {edit ? (
          children ? (
            children?.({ addTag, edit, setEdit, onKeyDown, onKeyUp })
          ) : (
            <FormControl maxW="250px" isInvalid={err}>
              <Popover initialFocusRef={inputRef} {...popDisc}>
                <PopoverTrigger>
                  <Input
                    borderRadius="full"
                    ref={inputRef}
                    onKeyDown={onKeyDown}
                    onKeyUp={onKeyUp}
                    onBlur={onTextBlur}
                    onChange={(e) => {
                      popDisc?.onOpen?.();
                      setStr(e.target.value);
                    }}
                    value={str}
                    autoFocus
                    {...props}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </PopoverTrigger>
                {dropdown && str?.length ? (
                  <PopoverContent>
                    <PopoverBody>
                      {dropdown?.({ addTag, setStr, str, dropdown: popDisc })}
                    </PopoverBody>
                  </PopoverContent>
                ) : (
                  ''
                )}
              </Popover>
              <FormErrorMessage>
                {t('createJob.enterSomeValue') || "Enter some value (max 50 chars)"}
              </FormErrorMessage>
            </FormControl>
          )
        ) : (
          <Button
            leftIcon={<CirclePlus width="20" height="20" />}
            p={6}
            borderRadius="full"
            variant="secondary"
            color="brandPrimary.500"
            onClick={(e) => setEdit(true)}
            bg="transparent"
            {...(buttonProps ?? {})}
          >
            {formatCommaSeparated([t('createJob.add') || 'Add', ''], ' ')}
          </Button>
        )}
      </HStack>
    </Stack>
  );
};
